import type {
  AutoEmailKey,
  AutoEmailSave,
  AutoEmailTemplate,
  NewFirmEmail,
  SupportCategory,
  SupportPriority,
  SupportSource,
  SupportStatus,
  SupportStatusColor,
  SupportStatusItem,
  SupportTicketRow,
  SupportView,
} from "../../slices/SupportSlice";

/* Support desk (owner 2026-10-25): labels and small rules for the Support page. */

export type PillTone =
  | "blue"
  | "red"
  | "amber"
  | "orange"
  | "purple"
  | "grey"
  | "green";

export const PILL_CLASSES: Record<PillTone, string> = {
  blue: "bg-[#EEF4FF] text-[#2F6FDB]",
  red: "bg-[#FDECEC] text-[#C0392B]",
  amber: "bg-[#FFF6E5] text-[#B7791F]",
  orange: "bg-[#FFEEE3] text-[#C2410C]",
  purple: "bg-[#F3F0FF] text-[#4B32D6]",
  grey: "bg-[#F1F2F5] text-[#5B6478]",
  green: "bg-[#E3F6EC] text-[#1E7F4A]",
};

export const CATEGORY_LABELS: Record<
  SupportCategory,
  { text: string; tone: PillTone }
> = {
  how_to: { text: "How-to question", tone: "blue" },
  cancellation: { text: "Cancellation", tone: "red" },
  billing: { text: "Billing", tone: "red" },
  privacy: { text: "Privacy request", tone: "red" },
  bug: { text: "Bug report", tone: "amber" },
  other: { text: "Other", tone: "grey" },
};

export const categoryPill = (category?: string | null) =>
  CATEGORY_LABELS[(category || "other") as SupportCategory] ||
  CATEGORY_LABELS.other;

export const SOURCE_LABELS: Record<SupportSource, string> = {
  email: "Email",
  chat: "Help chat",
  bug_report: "Problem report",
  website: "Website lead",
  onboarding: "New firm email",
  demo: "Demo follow-up",
  outbound: "New email",
};

export const sourceLabel = (source?: string | null) =>
  SOURCE_LABELS[(source || "email") as SupportSource] || "Email";

/** Help desk v2 (owner 2026-10-25): the case tabs. "Other mail" is shown apart (not a customer). */
export const VIEW_TABS: { view: SupportView; label: string }[] = [
  { view: "needs_you", label: "Needs you" },
  { view: "waiting", label: "Waiting on customer" },
  { view: "solved", label: "Solved" },
  { view: "closed", label: "Closed" },
  { view: "all", label: "All" },
];

/**
 * Case status filter (owner 2026-10-26, Loom): one dropdown next to Priority instead of pills.
 * Same views as VIEW_TABS plus "Other mail" last; counts in brackets like the old pills ("All" has none).
 */
export const statusFilterOptions = (
  counts: Partial<Record<SupportView, number>>,
): { value: SupportView; label: string }[] =>
  [
    ...VIEW_TABS,
    { view: "other" as SupportView, label: "Other mail" },
    // Schedule send (owner 2026-11-03): only when the server knows about it.
    ...(counts.scheduled != null
      ? [{ view: "scheduled" as SupportView, label: "Scheduled" }]
      : []),
  ].map((tab) => ({
    value: tab.view,
    label:
      tab.view === "all" || counts[tab.view] == null
        ? `Status: ${tab.label}`
        : `Status: ${tab.label} (${counts[tab.view]})`,
  }));

export const EMPTY_TEXT: Record<SupportView, { title: string; text: string }> =
  {
    needs_you: {
      title: "Nothing waiting for you",
      text: "New emails and chat requests show here. The inbox is checked every 2 minutes.",
    },
    waiting: {
      title: "No cases waiting on customers",
      text: "After you send a reply, the case waits here until the customer answers.",
    },
    solved: {
      title: "No solved cases",
      text: "Cases you mark Solved show here. They close by themselves after 7 days.",
    },
    closed: {
      title: "No closed cases",
      text: "Solved cases move here 7 days later. If the customer writes again, the case opens again.",
    },
    all: {
      title: "No cases yet",
      text: "Every case except Other mail shows here.",
    },
    other: {
      title: "No other mail",
      text: "Invoices, newsletters, robots and senders you skip land here, so they never clutter Needs you.",
    },
    scheduled: {
      title: "Nothing scheduled",
      text: "Emails you schedule (replies and new emails) wait here until they go out.",
    },
  };

export const STATUS_LABELS: Record<
  SupportStatus,
  { text: string; tone: PillTone }
> = {
  new: { text: "New", tone: "purple" },
  open: { text: "Open", tone: "blue" },
  waiting: { text: "Waiting on customer", tone: "amber" },
  solved: { text: "Solved", tone: "green" },
  closed: { text: "Closed", tone: "grey" },
  other: { text: "Other mail", tone: "grey" },
};

/** Older servers answer needs_you / done. */
export const statusPill = (status?: string | null) => {
  const s =
    status === "needs_you" ? "open" : status === "done" ? "closed" : status;
  return STATUS_LABELS[(s || "open") as SupportStatus] || STATUS_LABELS.open;
};

/* ---------- The owner's own statuses (owner 2026-10-26, gear on the Help desk) ---------- */

export const COLOR_TONES: Record<SupportStatusColor, PillTone> = {
  grey: "grey",
  blue: "blue",
  purple: "purple",
  yellow: "amber",
  orange: "orange",
  green: "green",
  red: "red",
};

export const COLOR_CHOICES: { value: SupportStatusColor; label: string }[] = [
  { value: "grey", label: "Grey" },
  { value: "blue", label: "Blue" },
  { value: "purple", label: "Purple" },
  { value: "yellow", label: "Yellow" },
  { value: "orange", label: "Orange" },
  { value: "green", label: "Green" },
  { value: "red", label: "Red" },
];

export const toneOf = (color?: string | null): PillTone =>
  COLOR_TONES[(color || "grey") as SupportStatusColor] || "grey";

/** The pill of a case: the owner's own status when it has one, else the main status. */
export const statusBadge = (
  row: Pick<
    SupportTicketRow,
    "status" | "customStatusId" | "statusName" | "statusColor"
  >,
) =>
  row.customStatusId && row.statusName
    ? { text: row.statusName, tone: toneOf(row.statusColor) }
    : statusPill(row.status);

/** Dropdown value: "g:<main status>" or "s:<own status id>". */
export const statusValue = (
  row: Pick<SupportTicketRow, "status" | "customStatusId">,
) =>
  row.customStatusId
    ? `s:${row.customStatusId}`
    : `g:${row.status === "new" ? "open" : row.status}`;

export const parseStatusValue = (
  value: string,
  statuses: SupportStatusItem[] = [],
): { status: SupportStatus; customStatusId: string | null } | null => {
  if (value.startsWith("s:")) {
    const own = statuses.find((s) => s.id === value.slice(2));
    return own
      ? {
          status: (own.group || "open") as SupportStatus,
          customStatusId: own.id,
        }
      : null;
  }
  const status = value.slice(2) as SupportStatus;
  return STATUS_CHOICES.includes(status) || status === "new"
    ? { status, customStatusId: null }
    : null;
};

/** The case status menu: each main status, then the owner's own statuses inside it. */
export const caseStatusGroups = (statuses: SupportStatusItem[] = []) =>
  STATUS_CHOICES.map((group) => ({
    group,
    label: statusPill(group).text,
    options: [
      { value: `g:${group}`, label: statusPill(group).text },
      ...statuses
        .filter((s) => s.kind === "case" && s.group === group)
        .sort((a, b) => a.order - b.order)
        .map((s) => ({ value: `s:${s.id}`, label: s.name })),
    ],
  }));

/** Same name rules as the server (1-40 characters, unique, not a main status). */
export const statusNameProblem = (
  name: string,
  kind: "case" | "firm",
  others: string[],
) => {
  const n = name.replace(/\s+/g, " ").trim();
  if (!n) return "Give the status a name.";
  if (n.length > 40) return "Status names can be at most 40 characters.";
  const main = ["new", "open", "waiting", "solved", "closed", "other mail"];
  if (kind === "case" && main.includes(n.toLowerCase()))
    return `“${n}” is already one of the main statuses. Pick another name.`;
  if (others.some((o) => o.trim().toLowerCase() === n.toLowerCase()))
    return `There is already a status called “${n}”.`;
  return "";
};

/** New firms dropdown value: "statusId" or "statusId:subStatusId". */
export const workChoiceValue = (
  statusId: string,
  subStatusId?: string | null,
) => (subStatusId ? `${statusId}:${subStatusId}` : statusId);

/** A pill in the work status's own colour (light background, coloured text); grey without a colour. */
export const workStatusPillStyle = (
  color?: string | null,
): { backgroundColor: string; color: string } => {
  const hex = /^#[0-9a-fA-F]{6}$/.test(color || "") ? (color as string) : "";
  if (!hex) return { backgroundColor: "#F1F2F5", color: "#5B6478" };
  return { backgroundColor: `${hex}1F`, color: hex };
};

/** Moves one id up (-1) or down (+1) in a list. */
export const moveId = (ids: string[], id: string, by: -1 | 1) => {
  const i = ids.indexOf(id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = [...ids];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

/** What the owner can set in the status menu (Other mail goes through "Not a customer"). */
export const STATUS_CHOICES: SupportStatus[] = [
  "open",
  "waiting",
  "solved",
  "closed",
];

export const PRIORITY_LABELS: Record<
  SupportPriority,
  { text: string; className: string }
> = {
  urgent: { text: "Urgent", className: "text-[#B42318]" },
  high: { text: "High", className: "text-[#B45309]" },
  normal: { text: "Normal", className: "text-[#3A4256]" },
  low: { text: "Low", className: "text-[#6B7185]" },
};

export const PRIORITIES: SupportPriority[] = [
  "urgent",
  "high",
  "normal",
  "low",
];

export const priorityLabel = (priority?: string | null) =>
  PRIORITY_LABELS[(priority || "normal") as SupportPriority] ||
  PRIORITY_LABELS.normal;

/** Cases waiting on the owner longer than this turn orange. */
export const LATE_AFTER_HOURS = 24;

/** "40 min" / "5 h" / "2 days" since the customer's newest message; late = over 24 hours (new / open only). */
export const waitingLabel = (
  row: Pick<SupportTicketRow, "status" | "lastInboundAt" | "lastMessageAt">,
  now: Date = new Date(),
): { text: string; late: boolean } => {
  const since = new Date(row.lastInboundAt || row.lastMessageAt).getTime();
  const minutes = Math.max(0, Math.floor((now.getTime() - since) / 60000));
  const text =
    minutes < 60
      ? `${Math.max(1, minutes)} min`
      : minutes < 48 * 60
      ? `${Math.floor(minutes / 60)} h`
      : `${Math.floor(minutes / (24 * 60))} days`;
  const waitingOnYou = row.status === "new" || row.status === "open";
  return { text, late: waitingOnYou && minutes >= LATE_AFTER_HOURS * 60 };
};

/** 200 -> "3 h 20 min"; 45 -> "45 min"; null -> "—". */
export const formatMinutes = (minutes?: number | null): string => {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h >= 48) return `${Math.round(h / 24)} days`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

/** "Northside Tax Co · 6 users", "From the help chat", or "Not a ProCharted user". */
export const customerLine = (
  row: Pick<SupportTicketRow, "firmName" | "firmPeople" | "source">,
): string => {
  if (row.firmName) {
    const people =
      row.firmPeople != null
        ? ` · ${row.firmPeople} user${row.firmPeople === 1 ? "" : "s"}`
        : "";
    return `${row.firmName}${people}`;
  }
  if (row.source === "chat") return "From the help chat";
  if (row.source === "website") return "From the procharted.com chat";
  if (row.source === "demo") return "Had a demo";
  return "Not a ProCharted user";
};

/** The line under the subject in the list. */
export const rowNote = (
  row: Pick<SupportTicketRow, "hasDraft" | "ownerOnly" | "preview" | "status"> &
    Partial<Pick<SupportTicketRow, "source">>,
): string => {
  const preview = row.preview || "";
  const waitingOnYou =
    row.status === "new" ||
    row.status === "open" ||
    (row.status as string) === "needs_you";
  if (!waitingOnYou) return preview;
  // Our own emails (welcome, check-in, demo follow-up) wait for the owner's review.
  if (row.hasDraft && (row.source === "onboarding" || row.source === "demo"))
    return `Draft to review: ${preview}`;
  if (row.hasDraft) return `AI draft ready: ${preview}`;
  if (row.ownerOnly) return `No AI draft (yours to answer): ${preview}`;
  return preview;
};

export const displayName = (
  row: Pick<SupportTicketRow, "contactName" | "contactEmail">,
) => (row.contactName || "").trim() || row.contactEmail;

export const MAX_REPLY = 20000;

/** Why Send is off, or "" when the reply can be sent. */
export const sendProblem = (text: string, mailboxConnected: boolean) => {
  if (!mailboxConnected) return "Connect the support mailbox first.";
  if (!text.trim()) return "Write a reply first.";
  if (text.length > MAX_REPLY)
    return `The reply is too long (max ${MAX_REPLY.toLocaleString(
      "en-US",
    )} characters).`;
  return "";
};

/** Text the server sent with an error (RTK Query), else the fallback. */
export const errorText = (error: unknown, fallback: string): string => {
  const data = (error as { data?: unknown } | undefined)?.data;
  if (typeof data === "string" && data.trim() && data.length < 400)
    return data.trim();
  return fallback;
};

/** The Talk-to-a-person form's limits (same as the server). */
export const CONTACT_MAX = 4000;
export const contactProblem = (message: string) => {
  if (!message.trim()) return "Please write your question.";
  if (message.length > CONTACT_MAX)
    return `Your message is too long (max ${CONTACT_MAX.toLocaleString(
      "en-US",
    )} characters).`;
  return "";
};

/** Puts a placeholder (or a saved reply) where the cursor is; returns the new text and cursor. */
export const insertAt = (
  text: string,
  start: number,
  end: number,
  value: string,
): { text: string; cursor: number } => {
  const a = Math.max(0, Math.min(start, text.length));
  const b = Math.max(a, Math.min(end, text.length));
  return {
    text: text.slice(0, a) + value + text.slice(b),
    cursor: a + value.length,
  };
};

/** The same checks as the server (Help desk v2 automatic emails). */
export const autoEmailProblem = (
  template: Pick<AutoEmailTemplate, "hasSubject" | "hasDelay">,
  form: AutoEmailSave,
): string => {
  if (template.hasSubject && form.subject != null) {
    const s = form.subject.trim();
    if (!s) return "Write a subject.";
    if (s.length > 300) return "The subject can be at most 300 characters.";
  }
  if (form.body != null) {
    const b = form.body.trim();
    if (!b) return "Write the email text.";
    if (b.length > MAX_REPLY)
      return `The email is too long (max ${MAX_REPLY.toLocaleString(
        "en-US",
      )} characters).`;
  }
  if (
    template.hasDelay &&
    form.delayDays != null &&
    (!Number.isInteger(form.delayDays) ||
      form.delayDays < 1 ||
      form.delayDays > 60)
  )
    return "Choose between 1 and 60 days.";
  if (form.linkUrl != null && form.linkUrl.trim()) {
    const u = form.linkUrl.trim();
    if (!/^https:\/\/[^\s]+\.[^\s]+$/i.test(u) || u.length > 500)
      return "The Get Started link must be a full https:// address.";
  }
  if (
    form.tips &&
    Object.values(form.tips).some((t) => (t || "").length > 2000)
  )
    return "Each tip can be at most 2,000 characters.";
  return "";
};

export const ONBOARDING_KEYS: AutoEmailKey[] = [
  "welcome",
  "checkin",
  "trial_ending",
];
export const CASE_EMAIL_KEYS: AutoEmailKey[] = ["ack", "nudge", "rating"];
export const DEMO_EMAIL_KEYS: AutoEmailKey[] = ["demo_followup"];

/** Demos tab (owner 2026-10-26): where each Calendly demo stands. */
export const demoStateLine = (row: {
  state: string;
  followUpDueAt?: string | null;
}): { text: string; tone: PillTone } => {
  switch (row.state) {
    case "completed":
      return {
        text: row.followUpDueAt
          ? `Done · follow-up draft ${shortDate(row.followUpDueAt)}`
          : "Done · follow-up off",
        tone: "green",
      };
    case "draft":
      return { text: "Follow-up draft ready ›", tone: "purple" };
    case "sent":
      return { text: "Follow-up sent ✓", tone: "green" };
    case "canceled":
      return { text: "Cancelled", tone: "grey" };
    default:
      return { text: "Demo booked", tone: "blue" };
  }
};

export const EMAIL_SHORT_NAMES: Record<AutoEmailKey, string> = {
  welcome: "Welcome",
  checkin: "Check-in",
  trial_ending: "Trial ending",
  demo_followup: "Demo follow-up",
  ack: "We got your message",
  nudge: "Nudge",
  rating: "How did we do?",
};

export const TIP_LABELS: { key: string; label: string }[] = [
  { key: "clients", label: "No clients yet" },
  { key: "work", label: "Clients added, no work item yet" },
  { key: "team", label: "Work added, team not invited" },
  { key: "done", label: "All steps done" },
];

/** "Day 3" / "Right away" / "When it happens" for the list of automatic emails. */
export const autoEmailWhen = (
  t: Pick<AutoEmailTemplate, "key" | "hasDelay" | "delayDays">,
) => {
  if (t.key === "welcome") return "Day 0";
  if (t.hasDelay)
    return t.key === "nudge" || t.key === "demo_followup"
      ? `${t.delayDays} days`
      : `Day ${t.delayDays}`;
  if (t.key === "ack") return "Instant";
  return "Solved";
};

const shortDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";

/** One onboarding email on the New firms tab: "Welcome ✓ Oct 20", "Check-in Oct 23", "Trial ending skipped (card added)". */
export const firmEmailLine = (
  e: NewFirmEmail,
): { text: string; tone: PillTone } => {
  const name = EMAIL_SHORT_NAMES[e.key as AutoEmailKey] || e.key;
  switch (e.state) {
    case "sent":
      return { text: `${name} ✓ ${shortDate(e.at)}`, tone: "green" };
    case "draft":
      return { text: `${name}: draft ready ›`, tone: "purple" };
    case "due":
      return { text: `${name} ${shortDate(e.at)}`, tone: "grey" };
    case "off":
      return { text: `${name} off`, tone: "grey" };
    case "skipped":
      return {
        text: `${name} skipped${e.note ? ` (${e.note.toLowerCase()})` : ""}`,
        tone: "amber",
      };
    default:
      return { text: name, tone: "grey" };
  }
};

/** A firm that signed up but hasn't finished setting up (owner 2026-10-28): the pill on the New firms row. */
export const firmSetupLine = (
  setup: string | null | undefined,
  signedUpAt: string,
): { text: string; note: string } | null => {
  if (setup === "account")
    return {
      text: "Hasn't set a password yet",
      note: `Signed up ${shortDate(
        signedUpAt,
      )}. Emails start when they finish setting up.`,
    };
  if (setup === "firm")
    return {
      text: "Hasn't filled in firm details yet",
      note: `Signed up ${shortDate(
        signedUpAt,
      )}. Emails start when they finish setting up.`,
    };
  return null;
};

export const STEP_LABELS: {
  key: "clients" | "work" | "team" | "card";
  label: string;
}[] = [
  { key: "clients", label: "Added clients" },
  { key: "work", label: "Created work" },
  { key: "team", label: "Invited team" },
  { key: "card", label: "Added a card" },
];

/* "Write with instructions" (owner 2026-10-27): the owner tells the AI what the reply must say. Same limit as the server. */
export const INSTRUCTIONS_MAX = 2000;

export const INSTRUCTION_EXAMPLES = [
  "Say sorry and that we'll fix it within 24 hours",
  "Make it shorter and friendlier",
  "Offer a 15-minute call this week",
];

export const instructionsProblem = (text: string) => {
  const t = text.trim();
  if (!t) return "Tell the AI what the reply should say.";
  if (t.length > INSTRUCTIONS_MAX)
    return `Keep it under ${INSTRUCTIONS_MAX.toLocaleString(
      "en-US",
    )} characters.`;
  return null;
};

// ---------- Email signatures (owner 2026-10-27) ----------

export const SIGNATURE_MAX = 1000;
export const SIGNATURE_MAX_LINES = 15;
/** The send request's value for "No signature". */
export const NO_SIGNATURE = "none";

/** Same as the server: line breaks kept, spaces at line ends and blank lines at the start/end removed. */
export const cleanSignature = (body: string) =>
  body
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .join("\n")
    .replace(/^[\n ]+|[\n ]+$/g, "");

/** Same checks as the server (SupportDeskService.SignatureProblem). */
export const signatureProblem = (name: string, body: string) => {
  const n = name.trim();
  const b = cleanSignature(body);
  if (!n) return "Give the signature a name.";
  if (n.length > 60) return "The name can be at most 60 characters.";
  if (b.length > SIGNATURE_MAX)
    return `The signature is too long (max ${SIGNATURE_MAX.toLocaleString()} characters).`;
  if (b.split("\n").length > SIGNATURE_MAX_LINES)
    return `A signature can have at most ${SIGNATURE_MAX_LINES} lines.`;
  return null;
};

/** The signature picked when the reply box opens: the default one, else none. */
export const initialSignatureChoice = (
  signatures: { id: string; isDefault: boolean }[] | undefined,
) => signatures?.find((s) => s.isDefault)?.id || NO_SIGNATURE;

/** Splits a line into text and https links (drawn like the email: links clickable). */
export const signatureParts = (line: string) => {
  const parts: { text: string; url?: string }[] = [];
  const re = /https?:\/\/[^\s<>"']+/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    const url = m[0].replace(/[.,);!?]+$/, "");
    if (m.index > last) parts.push({ text: line.slice(last, m.index) });
    parts.push({ text: url, url });
    last = m.index + url.length;
  }
  if (last < line.length) parts.push({ text: line.slice(last) });
  return parts;
};

/** "Send test" (owner 2026-10-30): the last 3 addresses, newest first, no duplicates (case ignored). */
export const TEST_ADDRESS_LIMIT = 3;
export const rememberTestAddress = (list: string[], address: string) => {
  const a = address.trim();
  if (!a) return list.slice(0, TEST_ADDRESS_LIMIT);
  return [a, ...list.filter((x) => x.toLowerCase() !== a.toLowerCase())].slice(
    0,
    TEST_ADDRESS_LIMIT,
  );
};
/** One plain email address, like the server's check. */
export const isTestAddress = (address: string) => {
  const a = address.trim();
  return (
    a.length > 3 &&
    a.length <= 254 &&
    /^[^@\s,;<>"]+@[^@\s,;<>"]+\.[^@\s,;<>"]+$/.test(a)
  );
};
